'use client';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorDialog } from '@/shared/components/ui/ErrorDialog';
import { registerEmail, type RegisterEmailRequest } from '@/shared/services/authServices';
import RecaptchaCheckbox, { type RecaptchaCheckboxHandle } from '@/shared/components/RecaptchaCheckbox';
import { isRecaptchaEnabled } from '@/shared/utils/recaptcha';
import TermsDialog from '@/shared/components/TermsDialog';
import { REFERRAL_STORAGE_KEY } from '@/shared/services/referralsService';
import { CaptchaModal, OtpInput, TermsCheck } from './CadastroParts';

type Step = 'form' | 'code' | 'success';

type EmailForm = {
  name: string;
  loginId: string;
  email: string;
  password: string;
  confirmPassword: string;
  referCode: string;
  acceptTerms: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CadastroEmail() {
  const navigate = useNavigate();
  const { showError } = useErrorDialog();

  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const captchaRef = useRef<RecaptchaCheckboxHandle>(null);

  // Etapa do codigo (2026-09-26: o cadastro por email era por LINK, virou
  // CODIGO igual ao celular). A conta so' e' criada depois do codigo.
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [dest, setDest] = useState('');
  const [error, setError] = useState<string | null>(null);
  const otpValid = otp.length === 6;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // O erro do servidor chega como {detail: {Status, Message}}
  const errMsg = (e: any, fallback: string) => {
    const b = (e?.detail && typeof e.detail === 'object') ? e.detail : e;
    return String(b?.Message || b?.message || e?.message || fallback);
  };

  const [form, setForm] = useState<EmailForm>({
    name: '',
    loginId: '',
    email: '',
    password: '',
    confirmPassword: '',
    referCode: '',
    acceptTerms: false,
  });

  const setField = <K extends keyof EmailForm>(k: K, v: EmailForm[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  // Auto-fill do código de referência:
  //  1. ?ref=ABC123 na URL → salva localStorage + preenche input
  //  2. Sem ?ref na URL → tenta localStorage (não perde o código se o user
  //     fechar e voltar mais tarde)
  // Limpeza do storage acontece após cadastro com sucesso (success step).
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const refFromUrl = url.searchParams.get('ref');
      if (refFromUrl) {
        const code = refFromUrl.trim().toUpperCase().slice(0, 10);
        if (code) {
          localStorage.setItem(REFERRAL_STORAGE_KEY, code);
          setForm((s) => ({ ...s, referCode: code }));
          return;
        }
      }
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (stored) setForm((s) => ({ ...s, referCode: stored }));
    } catch { /* localStorage indisponível — ignora */ }
  }, []);

  // Valida campos do form (NAO inclui captcha — captcha eh tratado depois)
  const validateFields = (): string | null => {
    const name = form.name.trim();
    const loginId = form.loginId.trim();
    const email = form.email.trim().toLowerCase();
    if (name.length < 3) return 'Informe nome (mín. 3 caracteres).';
    if (name.length > 30) return 'Informe nome (máx. 30 caracteres).';
    if (loginId.length < 4) return 'Informe um ID (mín. 4 caracteres).';
    if (loginId.length > 16) return 'Informe um ID (máx. 16 caracteres).';
    if (!EMAIL_RE.test(email)) return 'Email inválido.';
    if (form.password.length < 6) return 'Senha muito curta (mín. 6 caracteres).';
    if (form.password !== form.confirmPassword) return 'As senhas não conferem.';
    if (!form.acceptTerms) return 'Você precisa aceitar os termos para continuar.';
    return null;
  };

  const buildBody = (extra: Partial<RegisterEmailRequest>): RegisterEmailRequest => ({
    Username: form.loginId.trim(),
    Password: form.password,
    Email: form.email.trim().toLowerCase(),
    Name: form.name.trim(),
    ReferCode: form.referCode.trim() || null,
    ...extra,
  });

  // Passo 1 -- chamado apos o captcha (ou direto se desabilitado): o servidor
  // valida os dados e manda o CODIGO pro email. A conta ainda NAO existe.
  const doSubmit = async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await registerEmail(buildBody({ Captcha: token || null }));
      const st = String(res?.Status ?? res?.status ?? '');
      if (st === 'CODE_SENT') {
        setDest(String(res?.Destination ?? res?.destination ?? form.email));
        setOtp('');
        setCooldown(Number(res?.RetryAfterSeconds ?? res?.retryAfterSeconds ?? 60));
        setStep('code');
      } else {
        showError({ title: 'Falha no cadastro', message: res?.Message || res?.message || 'Não foi possível concluir o cadastro.' });
      }
    } catch (e: any) {
      showError({ title: 'Falha no cadastro', message: errMsg(e, 'Não foi possível concluir o cadastro. Tente novamente.') });
    } finally {
      // o token do captcha so' vale uma vez
      captchaRef.current?.reset();
      setCaptchaToken('');
      setLoading(false);
    }
  };

  // Passo 2 -- mesmos dados + codigo: so' agora a conta e' criada (ja' verificada).
  const verifyCode = async () => {
    if (!otpValid) { setError('Digite o código completo (6 dígitos).'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await registerEmail(buildBody({ Code: otp, Captcha: null }));
      const st = String(res?.Status ?? res?.status ?? '');
      if (st === 'Sucesso') {
        try { localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch { /* */ }
        setStep('success');
      } else {
        setError(res?.Message || res?.message || 'Não foi possível concluir o cadastro.');
      }
    } catch (e: any) {
      setError(errMsg(e, 'Código inválido ou expirado.'));
    } finally {
      setLoading(false);
    }
  };

  // Reenviar = passo 1 de novo. Com captcha ligado, pede o captcha outra vez
  // (o token anterior ja' foi consumido).
  const resend = () => {
    if (cooldown > 0 || loading) return;
    if (isRecaptchaEnabled()) { setShowCaptcha(true); return; }
    void doSubmit('');
  };

  // Quando o user resolve o captcha no modal, fecha + envia o form
  const handleCaptchaResolved = (token: string) => {
    setCaptchaToken(token);
    if (showCaptcha) {
      setShowCaptcha(false);
      // pequeno delay pra modal fechar antes de disparar request
      setTimeout(() => doSubmit(token), 150);
    }
  };

  const submit = async () => {
    const err = validateFields();
    if (err) { showError({ title: 'Verifique os campos', message: err }); return; }

    // Se captcha habilitado e ainda nao tem token → abre modal e PARA aqui
    if (isRecaptchaEnabled() && !captchaToken) {
      setShowCaptcha(true);
      return;
    }
    await doSubmit(captchaToken);
  };

  if (step === 'success') {
    return (
      <div>
        <div className="auth-note">
          <strong>Conta criada com sucesso!</strong>
          Seu e-mail <b>{form.email}</b> foi confirmado e sua conta já está ativa. Você já pode entrar.
        </div>
        <button type="button" className="btn btn-gold submit-btn" onClick={() => navigate('/')}>
          Voltar para a home
        </button>
      </div>
    );
  }

  if (step === 'code') {
    return (
      <div>
        <div className="auth-note">
          Enviamos um código para <b>{dest}</b>. Digite abaixo para confirmar seu e-mail.
          <br /><small>Não chegou? Confira também a caixa de spam.</small>
        </div>
        <OtpInput value={otp} onChange={setOtp} disabled={loading} length={6} />
        <div className="auth-links">
          <button type="button" onClick={() => { setError(null); setStep('form'); }} disabled={loading}>← Voltar e editar</button>
          <button type="button" onClick={resend} disabled={loading || cooldown > 0}>
            {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
          </button>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <button type="button" className="btn btn-gold submit-btn" onClick={verifyCode} disabled={!otpValid || loading}>
          {loading ? 'Validando...' : 'Confirmar cadastro'}
        </button>

        {showCaptcha && (
          <CaptchaModal
            loading={loading}
            text="Resolva o desafio abaixo para reenviar o código."
            onClose={() => setShowCaptcha(false)}
          >
            <RecaptchaCheckbox ref={captchaRef} onChange={(t) => { if (t) handleCaptchaResolved(t); }} theme="dark" />
          </CaptchaModal>
        )}
      </div>
    );
  }

  return (
    <form id="registerForm" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <div className="form-row">
        <label>Primeiro Nome<input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Primeiro nome" /></label>
        <label>ID do Jogo<input value={form.loginId} onChange={(e) => setField('loginId', e.target.value)} placeholder="Seu ID (login)" /></label>
      </div>
      <label>
        Email<input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="voce@exemplo.com" />
        <small>Enviaremos um código de confirmação para este e-mail.</small>
      </label>
      <div className="form-row">
        <label>Senha<input type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="••••••••" /></label>
        <label>Confirmar senha<input type="password" value={form.confirmPassword} onChange={(e) => setField('confirmPassword', e.target.value)} placeholder="••••••••" /></label>
      </div>
      <label>
        Código de referência <em>(opcional)</em>
        <input
          value={form.referCode}
          onChange={(e) => setField('referCode', e.target.value.toUpperCase().slice(0, 10))}
          placeholder="Ex.: ABC12345"
          maxLength={10}
        />
        <small>Recebeu um link de um amigo? O código entra aqui automaticamente.</small>
      </label>
      <TermsCheck checked={form.acceptTerms} onChange={(v) => setField('acceptTerms', v)} onOpenTerms={() => setShowTerms(true)} />
      <button className="btn btn-gold submit-btn" type="submit" disabled={loading}>
        {loading ? 'Enviando...' : 'Criar conta'}
      </button>

      <TermsDialog open={showTerms} onClose={() => setShowTerms(false)} />

      {/* Modal do reCAPTCHA — abre só quando o user clica Criar conta */}
      {showCaptcha && (
        <CaptchaModal
          loading={loading}
          text="Resolva o desafio abaixo para criar sua conta."
          onClose={() => setShowCaptcha(false)}
        >
          <RecaptchaCheckbox ref={captchaRef} onChange={(t) => { if (t) handleCaptchaResolved(t); }} theme="dark" />
        </CaptchaModal>
      )}
    </form>
  );
}
